import React, { Component } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Input,
  InputGroup,
  InputGroupAddon,
} from 'reactstrap';

/**
 * WhitelistModal
 *
 * Lets the admin view, add, and remove individual whitelisted sender
 * email addresses for a Gmail account. The backend stores them as an
 * array of strings.
 */
class WhitelistModal extends Component {
  state = {
    senders: [],
    newEmail: '',
    inputError: '',
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      // Copy the array prop into local state when the modal opens
      const senders = Array.isArray(this.props.whitelistedSenders)
        ? [...this.props.whitelistedSenders]
        : [];
      this.setState({ senders, newEmail: '', inputError: '' });
    }
  }

  handleInputChange = (e) => {
    this.setState({ newEmail: e.target.value, inputError: '' });
  };

  handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.addEmail();
    }
  };

  addEmail = () => {
    const { newEmail, senders } = this.state;
    const trimmed = newEmail.trim().toLowerCase();

    if (!trimmed) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      this.setState({ inputError: 'Please enter a valid email address.' });
      return;
    }

    if (senders.includes(trimmed)) {
      this.setState({ inputError: 'This email is already in the whitelist.' });
      return;
    }

    this.setState({
      senders: [...senders, trimmed],
      newEmail: '',
      inputError: '',
    });
  };

  removeEmail = (email) => {
    this.setState((prev) => ({
      senders: prev.senders.filter((s) => s !== email),
    }));
  };

  handleSave = () => {
    this.props.onSave(this.props.emailAddress, this.state.senders);
  };

  render() {
    const { isOpen, onClose, emailAddress, saving } = this.props;
    const { senders, newEmail, inputError } = this.state;

    return (
      <Modal isOpen={isOpen} toggle={onClose} size="md">
        <ModalHeader toggle={onClose}>
          Whitelisted Senders
          <br />
          <small className="text-muted">{emailAddress}</small>
        </ModalHeader>

        <ModalBody>
          <p className="text-muted small mb-3">
            Only emails from these senders will be synced. Add or remove
            addresses below.
          </p>

          {/* Sender tags */}
          <div
            style={{
              minHeight: '60px',
              border: '1px solid #e3e3e3',
              borderRadius: '4px',
              padding: '8px',
              marginBottom: '12px',
              background: '#fafafa',
            }}
          >
            {senders.length === 0 ? (
              <span className="text-muted small">
                No whitelisted senders — all incoming mail will be synced.
              </span>
            ) : (
              senders.map((email) => (
                <Badge
                  key={email}
                  color="info"
                  className="mr-1 mb-1"
                  style={{
                    fontSize: '0.85rem',
                    padding: '6px 10px',
                    fontWeight: 'normal',
                  }}
                >
                  {email}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => this.removeEmail(email)}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && this.removeEmail(email)
                    }
                    style={{
                      marginLeft: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                    }}
                    title={`Remove ${email}`}
                  >
                    &times;
                  </span>
                </Badge>
              ))
            )}
          </div>

          {/* Add new email */}
          <InputGroup>
            <Input
              type="email"
              placeholder="Enter email address..."
              value={newEmail}
              onChange={this.handleInputChange}
              onKeyDown={this.handleKeyDown}
              invalid={!!inputError}
            />
            <InputGroupAddon addonType="append">
              <Button color="primary" size="sm" onClick={this.addEmail}>
                Add
              </Button>
            </InputGroupAddon>
          </InputGroup>
          {inputError && (
            <small className="text-danger mt-1 d-block">{inputError}</small>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" onClick={this.handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default WhitelistModal;
